import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export const PAGE_SIZE_AUTO = 'auto';
const DEFAULT_MANUAL_SIZES = [10, 25, 50];

/**
 * Clamp a fitted row count into the allowed page-size range.
 * Do not snap 11–14 back to 10 — that left large monitors stuck on Auto (10).
 */
export function clampPageSize(rawCount, { minSize = 10, maxSize = 50 } = {}) {
  const n = Number(rawCount);
  if (!Number.isFinite(n)) return minSize;
  return Math.min(maxSize, Math.max(minSize, Math.floor(n)));
}

/**
 * Measure how many table rows fit in the visible list area.
 *
 * Attach `containerRef` to the full table card (rows + pagination). We subtract
 * thead + pagination, and also compare against window space below the card top
 * so content-sized flex children cannot trap Auto at 10 on large monitors.
 */
export default function useViewportPageSize(options = {}) {
  const {
    rowHeight = 48,
    headerHeight = 42,
    reservedBottom = 56,
    minSize = 10,
    maxSize = 50,
    manualSizes = DEFAULT_MANUAL_SIZES,
    debounceMs = 150,
    enabled = true,
    initialSize = minSize,
    defaultMode = PAGE_SIZE_AUTO,
    stabilityDelta = 1,
  } = options;

  const [node, setNode] = useState(null);
  const [autoSize, setAutoSize] = useState(initialSize);
  const [overrideSize, setOverrideSize] = useState(() => (
    defaultMode === PAGE_SIZE_AUTO ? null : Number(defaultMode)
  ));
  const debounceTimerRef = useRef(null);

  const containerRef = useCallback((el) => {
    setNode((prev) => (prev === el ? prev : el));
  }, []);

  const measure = useCallback(() => {
    if (!enabled || !node || typeof window === 'undefined') return;

    const rect = node.getBoundingClientRect();
    const paginationEl = node.querySelector('[aria-label="pagination"]');
    const paginationH = paginationEl
      ? Math.ceil(paginationEl.getBoundingClientRect().height)
      : reservedBottom;

    const clientBased = Math.max(0, node.clientHeight - headerHeight - paginationH);
    const windowBased = Math.max(
      0,
      window.innerHeight - rect.top - paginationH - headerHeight
    );
    const available = Math.max(clientBased, windowBased);
    const next = clampPageSize(available / Math.max(rowHeight, 1), { minSize, maxSize });

    setAutoSize((prev) => {
      if (Math.abs(prev - next) <= stabilityDelta) return prev;
      return next;
    });
  }, [
    enabled,
    headerHeight,
    maxSize,
    minSize,
    node,
    reservedBottom,
    rowHeight,
    stabilityDelta,
  ]);

  useEffect(() => {
    if (!enabled || !node) return undefined;

    const schedule = () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = setTimeout(measure, debounceMs);
    };

    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        measure();
        schedule();
      });
    });

    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(schedule) : null;
    observer?.observe(node);
    window.addEventListener('resize', schedule);

    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      observer?.disconnect();
      window.removeEventListener('resize', schedule);
    };
  }, [debounceMs, enabled, measure, node]);

  const isAuto = overrideSize == null;
  const pageSize = isAuto ? autoSize : overrideSize;
  const pageSizeMode = isAuto ? PAGE_SIZE_AUTO : overrideSize;

  const setPageSizeMode = useCallback((value) => {
    if (value === PAGE_SIZE_AUTO || value === 'auto') {
      setOverrideSize(null);
      return;
    }
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return;
    setOverrideSize(Math.min(maxSize, Math.max(minSize, Math.round(numeric))));
  }, [maxSize, minSize]);

  const pageSizeOptions = useMemo(() => {
    const manual = manualSizes
      .filter((size) => size >= minSize && size <= maxSize)
      .map((size) => ({ value: size, label: String(size) }));

    return [
      { value: PAGE_SIZE_AUTO, label: `Auto (${autoSize})` },
      ...manual,
    ];
  }, [autoSize, manualSizes, maxSize, minSize]);

  return {
    containerRef,
    pageSize,
    autoSize,
    pageSizeMode,
    isAuto,
    setPageSizeMode,
    setPageSize: setPageSizeMode,
    pageSizeOptions,
  };
}
